import { useEffect, useState } from "react";
import { api, formatRupiah, formatDate, errMsg, downloadPdf } from "@/lib/api";
import { Plus, X, Trash2, Calculator, FileDown, MessageCircle } from "lucide-react";

const DOC_LABEL = { SP: "Surat Pesanan", INV: "Invoice", KWT: "Kuitansi" };

export default function Quotations() {
  const [list, setList] = useState([]);
  const [docs, setDocs] = useState([]);
  const [view, setView] = useState("quotations");
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    client_id: "", project_name: "", segment: "Wedding", event_date: "",
    items: [{ name: "", qty: 1, unit_price: 0, hpp: 0 }],
    discount: 0, ppn_enabled: false, ppn_rate: 11, termin: "30/40/30", notes: ""
  });

  async function load() {
    const [q, c, d] = await Promise.all([api.get("/quotations"), api.get("/clients"), api.get("/documents")]);
    setList(q.data); setClients(c.data); setDocs(d.data);
  }
  useEffect(() => { load(); }, []);

  const subtotal = form.items.reduce((s, i) => s + Number(i.qty) * Number(i.unit_price), 0);
  const hpp = form.items.reduce((s, i) => s + Number(i.qty) * Number(i.hpp), 0);
  const afterDisc = subtotal - Number(form.discount || 0);
  const ppn = form.ppn_enabled ? afterDisc * (Number(form.ppn_rate) / 100) : 0;
  const total = afterDisc + ppn;
  const margin = afterDisc > 0 ? ((afterDisc - hpp) / afterDisc) * 100 : 0;
  const minMargin = form.segment === "Wedding" ? 15 : 20;
  const low = margin < minMargin;

  function updateItem(idx, field, val) {
    const items = [...form.items];
    items[idx][field] = val;
    setForm({ ...form, items });
  }

  async function submit(e) {
    e.preventDefault();
    try {
      const items = form.items.map(i => ({ ...i, qty: Number(i.qty), unit_price: Number(i.unit_price), hpp: Number(i.hpp) }));
      await api.post("/quotations", { ...form, items, discount: Number(form.discount) });
      setOpen(false);
      setForm({ client_id: "", project_name: "", segment: "Wedding", event_date: "",
        items: [{ name: "", qty: 1, unit_price: 0, hpp: 0 }],
        discount: 0, ppn_enabled: false, ppn_rate: 11, termin: "30/40/30", notes: "" });
      await load();
    } catch (ex) { setErr(errMsg(ex)); }
  }

  async function shareWa(d) {
    const win = window.open("", "_blank");
    try {
      const { data } = await api.post(`/documents/${d.id}/share`);
      const link = `${process.env.REACT_APP_BACKEND_URL}${data.path}`;
      const label = d.type === "INV" ? "Invoice" : "Kuitansi";
      const nominal = formatRupiah(d.termin_amount || d.total);
      const text = `Halo ${data.client_name || ""},\n\nBerikut ${label} ${d.number} untuk ${d.project_name}${d.termin_label ? ` (${d.termin_label})` : ""} sebesar ${nominal}.\n\nUnduh PDF: ${link}\n\nTerima kasih,\nPuade Decoration`;
      const url = `https://wa.me/${data.phone || ""}?text=${encodeURIComponent(text)}`;
      if (win) win.location.href = url; else window.location.href = url;
    } catch (ex) { win?.close(); setErr(errMsg(ex)); }
  }

  async function convert(q, type) {
    let body = {};
    if (type !== "SP") {
      const label = window.prompt("Keterangan termin (mis. DP 30%, Termin 2, Pelunasan)", "DP 30%");
      if (label === null) return;
      const amt = window.prompt("Nominal termin (Rp)", String(Math.round(q.total * 0.3)));
      if (amt === null) return;
      body = { termin_label: label, termin_amount: Number(amt) || 0 };
    }
    try {
      await api.post(`/quotations/${q.id}/convert/${type}`, body);
      await load();
      setView("documents");
    } catch (ex) { setErr(errMsg(ex)); }
  }

  return (
    <div className="p-4 sm:p-8">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif-display font-bold">Quotation</h1>
          <p className="text-sm text-[#536263]">Builder dengan kalkulator HPP &amp; margin real-time</p>
          <div className="mt-2 h-px w-12 bg-[#B87333]"></div>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary" data-testid="quotation-add-btn"><Plus size={18} />Quotation Baru</button>
      </header>

      <div className="flex gap-2 mb-4">
        {[["quotations", `Quotation (${list.length})`], ["documents", `Dokumen (${docs.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`quotation-view-${k}`}
            className={`chip ${view === k ? "bg-[#0F5257] text-white" : "bg-[#F2EEE7] text-[#536263]"}`}>{l}</button>
        ))}
      </div>
      {err && !open && <div className="text-sm text-red-700 mb-3">{err}</div>}

      {view === "documents" && (
        <div className="space-y-2" data-testid="documents-list">
          {docs.map(d => (
            <div key={d.id} className="card-editorial p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><span className="chip bg-[#F7EFE8] text-[#B87333]">{DOC_LABEL[d.type]}</span><span className="text-xs font-mono-num text-[#8A999A]">{d.number}</span></div>
                <div className="font-medium mt-1 truncate">{d.project_name}</div>
                <div className="text-xs text-[#8A999A]">{formatDate(d.created_at)}{d.termin_label ? ` · ${d.termin_label}` : ""}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono-num font-bold text-[#0F5257]">{formatRupiah(d.termin_amount || d.total)}</div>
                <div className="flex gap-1 justify-end mt-1">
                  {["INV", "KWT"].includes(d.type) && <button onClick={() => shareWa(d)} className="chip bg-[#25D366]/15 text-[#128C7E]" data-testid={`doc-wa-${d.id}`}><MessageCircle size={14} />WhatsApp</button>}
                  <button onClick={() => downloadPdf(`/documents/${d.id}/pdf`, d.number)} className="chip bg-[#0F5257] text-white" data-testid={`doc-pdf-${d.id}`}><FileDown size={14} />PDF</button>
                </div>
              </div>
            </div>
          ))}
          {docs.length === 0 && <div className="text-center p-12 text-[#8A999A]">Belum ada dokumen</div>}
        </div>
      )}

      {view === "quotations" && <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="quotations-list">
        {list.map(q => (
          <div key={q.id} className="card-editorial p-5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-mono-num text-[#B87333]">{q.number}</span>
              <span className={`chip ${q.margin_warning ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>Margin {q.margin_percent}%</span>
            </div>
            <h3 className="font-serif-display text-lg font-semibold">{q.project_name}</h3>
            <p className="text-sm text-[#536263]">{q.segment} · {q.items?.length || 0} item</p>
            <div className="mt-3 flex items-center justify-between">
              <span className="font-mono-num text-lg font-bold text-[#0F5257]">{formatRupiah(q.total)}</span>
              <div className="flex gap-2 flex-wrap justify-end">
                <button onClick={() => downloadPdf(`/quotations/${q.id}/pdf`, q.number)} className="chip bg-[#0F5257] text-white" data-testid={`quotation-pdf-${q.id}`}><FileDown size={14} />PDF</button>
                <button onClick={() => convert(q, "SP")} className="chip bg-[#F7EFE8] text-[#B87333]" data-testid={`convert-sp-${q.id}`}>→ SP</button>
                <button onClick={() => convert(q, "INV")} className="chip bg-[#F7EFE8] text-[#B87333]" data-testid={`convert-inv-${q.id}`}>→ INV</button>
                <button onClick={() => convert(q, "KWT")} className="chip bg-[#F7EFE8] text-[#B87333]" data-testid={`convert-kwt-${q.id}`}>→ KWT</button>
              </div>
            </div>
            {q.margin_warning && <div className="mt-2 text-xs text-red-700">⚠ Butuh approval Owner (margin &lt; {q.min_margin}%)</div>}
          </div>
        ))}
        {list.length === 0 && <div className="col-span-full text-center p-12 text-[#8A999A]">Belum ada quotation</div>}
      </div>}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-0 sm:p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <form onSubmit={submit} onClick={e => e.stopPropagation()} className="bg-white rounded-t-2xl sm:rounded-2xl w-full sm:max-w-2xl my-0 sm:my-6 p-5 space-y-4" data-testid="quotation-form">
            <div className="flex items-center justify-between">
              <h3 className="font-serif-display text-xl font-bold flex items-center gap-2"><Calculator size={20} />Quotation Baru</h3>
              <button type="button" onClick={() => setOpen(false)}><X /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <select required value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} className="border border-[#E2DDD3] rounded-lg px-3 h-11">
                <option value="">Pilih Klien</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <select value={form.segment} onChange={e => setForm({...form, segment: e.target.value})} className="border border-[#E2DDD3] rounded-lg px-3 h-11">
                {["Wedding","Corporate","Brand Activation","Expo","Lainnya"].map(x => <option key={x}>{x}</option>)}
              </select>
            </div>
            <input required placeholder="Nama Project" value={form.project_name} onChange={e => setForm({...form, project_name: e.target.value})} className="w-full border border-[#E2DDD3] rounded-lg px-3 h-11" />

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-semibold text-sm">Items</h4>
                <button type="button" onClick={() => setForm({...form, items: [...form.items, { name: "", qty: 1, unit_price: 0, hpp: 0 }]})}
                  className="text-xs chip bg-[#F7EFE8] text-[#B87333]" data-testid="add-item-btn">+ Item</button>
              </div>
              {form.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-1 mb-2 items-center">
                  <input required placeholder="Nama" value={it.name} onChange={e => updateItem(idx, "name", e.target.value)}
                    className="col-span-5 border border-[#E2DDD3] rounded-lg px-2 h-10 text-sm" />
                  <input type="number" placeholder="Qty" value={it.qty} onChange={e => updateItem(idx, "qty", e.target.value)}
                    className="col-span-1 border border-[#E2DDD3] rounded-lg px-2 h-10 text-sm" />
                  <input type="number" placeholder="Harga" value={it.unit_price} onChange={e => updateItem(idx, "unit_price", e.target.value)}
                    className="col-span-3 border border-[#E2DDD3] rounded-lg px-2 h-10 text-sm" />
                  <input type="number" placeholder="HPP" value={it.hpp} onChange={e => updateItem(idx, "hpp", e.target.value)}
                    className="col-span-2 border border-[#E2DDD3] rounded-lg px-2 h-10 text-sm" />
                  <button type="button" onClick={() => setForm({...form, items: form.items.filter((_,i) => i !== idx)})}
                    className="col-span-1 text-red-700"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <input type="number" placeholder="Diskon" value={form.discount} onChange={e => setForm({...form, discount: e.target.value})} className="border border-[#E2DDD3] rounded-lg px-3 h-11" />
              <label className="flex items-center gap-2 border border-[#E2DDD3] rounded-lg px-3 h-11">
                <input type="checkbox" checked={form.ppn_enabled} onChange={e => setForm({...form, ppn_enabled: e.target.checked})} />
                <span className="text-sm">PPN {form.ppn_rate}%</span>
              </label>
            </div>

            <div className="p-4 bg-[#FAF7F2] rounded-lg space-y-1 text-sm" data-testid="quotation-calc">
              <Row l="Subtotal" v={formatRupiah(subtotal)} />
              <Row l="HPP" v={formatRupiah(hpp)} />
              <Row l="Setelah Diskon" v={formatRupiah(afterDisc)} />
              {form.ppn_enabled && <Row l={`PPN ${form.ppn_rate}%`} v={formatRupiah(ppn)} />}
              <div className="border-t border-[#E2DDD3] pt-2 mt-2">
                <Row l="Total" v={formatRupiah(total)} bold />
                <Row l={`Margin (min ${minMargin}%)`} v={<span className={low ? "text-red-700" : "text-emerald-700"}>{margin.toFixed(2)}%</span>} bold />
              </div>
            </div>

            {err && <div className="text-sm text-red-700">{err}</div>}
            <button type="submit" className="btn-primary w-full" data-testid="quotation-submit">Simpan Quotation</button>
          </form>
        </div>
      )}
    </div>
  );
}
const Row = ({ l, v, bold }) => <div className={`flex justify-between ${bold ? "font-semibold" : ""}`}><span>{l}</span><span className="font-mono-num">{v}</span></div>;
